import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Quote from '@editorjs/quote';
import Delimiter from '@editorjs/delimiter';
import Table from '@editorjs/table';
import CodeTool from '@editorjs/code';
import InlineCode from '@editorjs/inline-code';
import Marker from '@editorjs/marker';
import Underline from '@editorjs/underline';
import { React, useEffect, useRef, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import UserContext from '../../components/User_Context.js';
import * as menu from "../../assets/menus/menu.js";
import * as api from "../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import EditorMiniChat from './EditorMiniChat.jsx';
import EditorToolbar from './EditorToolbar.js';
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { calculateHeaderHeight } from '../../components/ui_utils.js';
import './EditorJS.css';



const Editor = () => {
    const editorInstance = useRef(null);
    const editorRef = useRef(null);
    const { currentUser } = useContext(UserContext);
    const { id = null } = useParams();
    const [data, setData] = useState(null);
    const { t } = useTranslation('labels');
    const [fontSize, setFontSize] = useState('16');
    const [fontFamily, setFontFamily] = useState('Calibri');
    const [saving, setSaving] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const MySwal = withReactContent(Swal);
    const [headerHeight, setHeaderHeight] = useState(0);

    // New states for AI integration
    const [isAiModifying, setIsAiModifying] = useState(false);
    const [lastAiUpdate, setLastAiUpdate] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [pendingAiChanges, setPendingAiChanges] = useState(new Map());
    
    // History management for undo/redo functionality
    const [documentHistory, setDocumentHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [isRestoringFromHistory, setIsRestoringFromHistory] = useState(false);

    //------------------------------------------------------------------------------

    // History management functions - SIMPLIFIED APPROACH
    const saveSnapshot = async () => {
        if (!editorInstance.current || isRestoringFromHistory) {
            console.log('💾 Snapshot skipped:', {
                noEditor: !editorInstance.current,
                isRestoring: isRestoringFromHistory
            });
            return;
        }

        try {
            console.log('💾 Saving snapshot...');
            const currentData = await editorInstance.current.save();
            console.log('📄 Current data blocks:', currentData.blocks?.length || 0);
            
            // Update both history and index in a single batched update
            const newSnapshot = {
                data: currentData,
                timestamp: new Date().toISOString(),
                blocks: currentData.blocks?.length || 0
            };
            
            // Update history first
            setDocumentHistory(prevHistory => {
                const currentIndex = historyIndex; // Capture current value
                console.log('📚 Using historyIndex:', currentIndex, 'prevHistory.length:', prevHistory.length);
                
                // Keep history up to current index, add new snapshot
                const newHistory = [...prevHistory.slice(0, currentIndex + 1), newSnapshot];
                
                console.log('📚 New history:', {
                    oldLength: prevHistory.length,
                    newLength: newHistory.length,
                    currentIndex: currentIndex,
                    nextIndex: currentIndex + 1
                });
                
                // Keep maximum 50 snapshots
                if (newHistory.length > 50) {
                    newHistory.shift();
                }
                
                return newHistory;
            });
            
            // Then update index
            setHistoryIndex(prev => {
                const newIndex = prev + 1;
                console.log('📊 Index update:', prev, '->', newIndex);
                return newIndex;
            });
            
        } catch (error) {
            console.error('❌ Error saving snapshot:', error);
        }
    };

    const undo = async () => {
        console.log('⏪ Undo called - checking conditions:', {
            historyIndex: historyIndex,
            canUndo: historyIndex > 0,
            hasEditor: !!editorInstance.current,
            historyLength: documentHistory.length
        });
        
        if (historyIndex <= 0 || !editorInstance.current) {
            console.warn('⏪ Cannot undo:', {
                historyIndex: historyIndex,
                hasEditor: !!editorInstance.current
            });
            return false;
        }

        try {
            setIsRestoringFromHistory(true);
            const previousSnapshot = documentHistory[historyIndex - 1];
            
            console.log('📸 Previous snapshot:', {
                exists: !!previousSnapshot,
                timestamp: previousSnapshot?.timestamp,
                blocks: previousSnapshot?.blocks || 0
            });
            
            if (previousSnapshot) {
                console.log('🔄 Restoring previous state...');
                await editorInstance.current.clear();
                await editorInstance.current.render(previousSnapshot.data);
                setHistoryIndex(prev => {
                    const newIndex = prev - 1;
                    console.log('📊 History index updated:', prev, '->', newIndex);
                    return newIndex;
                });
                setHasUnsavedChanges(true);
                console.log('✅ Undo successful');
                return true;
            } else {
                console.error('❌ No previous snapshot found at index', historyIndex - 1);
            }
        } catch (error) {
            console.error('❌ Error during undo:', error);
        } finally {
            setIsRestoringFromHistory(false);
        }
        
        return false;
    };

    const redo = async () => {
        if (historyIndex >= documentHistory.length - 1 || !editorInstance.current) return false;

        try {
            setIsRestoringFromHistory(true);
            const nextSnapshot = documentHistory[historyIndex + 1];
            
            if (nextSnapshot) {
                await editorInstance.current.clear();
                await editorInstance.current.render(nextSnapshot.data);
                setHistoryIndex(prev => prev + 1);
                setHasUnsavedChanges(true);
                return true;
            }
        } catch (error) {
            console.error('Error during redo:', error);
        } finally {
            setIsRestoringFromHistory(false);
        }
        
        return false;
    };

    // Handle document updates from AI
    const handleDocumentUpdate = async (newDocumentState, aiChange = null) => {
        console.log('🤖 AI Document Update:', {
            hasAiChange: !!aiChange,
            changeId: aiChange?.id,
            isRestoring: isRestoringFromHistory,
            newStateBlocks: newDocumentState?.blocks?.length || 0
        });
        
        if (!editorInstance.current) return;

        try {
            setIsAiModifying(true);
            
            // Save snapshot before AI modification (only if it's a new AI change, not a restoration)
            let beforeSnapshotIndex = historyIndex;
            if (aiChange && !isRestoringFromHistory) {
                console.log('💾 Saving snapshot before AI change... Current historyIndex:', historyIndex);
                await saveSnapshot();
                // After saveSnapshot, historyIndex will be incremented
                console.log('💾 Snapshot saved, historyIndex should now be:', historyIndex);
            }
            
            setLastAiUpdate(new Date());
            setHasUnsavedChanges(true);

            // Track AI changes if provided
            if (aiChange) {
                setPendingAiChanges(prev => {
                    const newMap = new Map(prev);
                    newMap.set(aiChange.id, {
                        ...aiChange,
                        timestamp: new Date().toISOString()
                    });
                    console.log('📝 Added pending AI change:', {
                        changeId: aiChange.id,
                        timestamp: new Date().toISOString()
                    });
                    return newMap;
                });
            }

            // Clear current editor content and render new state
            console.log('🔄 Rendering new document state...');
            await editorInstance.current.clear();
            await editorInstance.current.render(newDocumentState);
            console.log('✅ Document updated successfully');

            // Show notification only for AI changes
            if (aiChange) {
                MySwal.fire({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                    icon: 'info',
                    title: 'Modifica AI Applicata',
                    text: 'L\'AI ha modificato il documento. Usa i controlli per accettare/rifiutare.'
                });
            }

        } catch (error) {
            console.error('❌ Error updating document from AI:', error);
            MySwal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                icon: 'error',
                title: 'Errore aggiornamento',
                text: 'Errore durante l\'aggiornamento del documento'
            });
        } finally {
            setIsAiModifying(false);
        }
    };

    // Handle AI change acceptance/rejection
    const handleAiChangeAccepted = async (changeId) => {
        // Accept the change - save a new snapshot to confirm the current state
        await saveSnapshot();
        
        setPendingAiChanges(prev => {
            const newMap = new Map(prev);
            newMap.delete(changeId);
            return newMap;
        });

        MySwal.fire({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2000,
            icon: 'success',
            title: 'Modifica Accettata',
            text: 'Le modifiche AI sono state confermate.'
        });
    };

    const handleAiChangeRejected = async (changeId) => {
        console.log('🔄 Rejecting AI change:', changeId);
        console.log('📊 Current history state:', {
            historyLength: documentHistory.length,
            historyIndex: historyIndex,
            canUndo: historyIndex > 0
        });
        
        // Get the change details before removing it
        const aiChange = pendingAiChanges.get(changeId);
        console.log('📝 AI Change details:', aiChange);
        
        // Remove from pending changes first
        setPendingAiChanges(prev => {
            const newMap = new Map(prev);
            newMap.delete(changeId);
            return newMap;
        });

        // Perform undo to restore previous state
        console.log('⏪ Attempting undo...');
        const undoSuccess = await undo();
        console.log('✅ Undo result:', undoSuccess);
        
        if (undoSuccess) {
            MySwal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2000,
                icon: 'info',
                title: 'Modifica Rifiutata',
                text: 'Il documento è stato ripristinato allo stato precedente.'
            });
        } else {
            console.error('❌ Undo failed - history info:', {
                historyLength: documentHistory.length,
                historyIndex: historyIndex
            });
            MySwal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                icon: 'warning',
                title: 'Impossibile ripristinare',
                text: 'Non è stato possibile ripristinare lo stato precedente.'
            });
        }
    };

    // Accept all AI changes at once
    const handleAcceptAllAiChanges = async () => {
        if (pendingAiChanges.size === 0) return;

        MySwal.fire({
            title: 'Conferma tutte le modifiche',
            text: `Vuoi accettare tutte le ${pendingAiChanges.size} modifiche AI?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sì, accetta tutto',
            cancelButtonText: 'Annulla'
        }).then(async (result) => {
            if (result.isConfirmed) {
                // Accept all changes
                setPendingAiChanges(new Map());
                await saveSnapshot();
                
                MySwal.fire({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                    icon: 'success',
                    title: 'Tutte le modifiche accettate',
                    text: 'Tutte le modifiche AI sono state confermate.'
                });
            }
        });
    };

    // Reject all AI changes at once
    const handleRejectAllAiChanges = async () => {
        if (pendingAiChanges.size === 0) return;

        MySwal.fire({
            title: 'Rifiuta tutte le modifiche',
            text: `Vuoi rifiutare tutte le ${pendingAiChanges.size} modifiche AI? Il documento tornerà al suo stato precedente.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sì, rifiuta tutto',
            cancelButtonText: 'Annulla'
        }).then(async (result) => {
            if (result.isConfirmed) {
                // Clear all pending changes
                setPendingAiChanges(new Map());
                
                // Perform multiple undos to go back to the state before all AI changes
                let undoCount = 0;
                const maxUndos = Math.min(pendingAiChanges.size, historyIndex);
                
                for (let i = 0; i < maxUndos; i++) {
                    const success = await undo();
                    if (success) undoCount++;
                    else break;
                }
                
                MySwal.fire({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                    icon: 'info',
                    title: 'Tutte le modifiche rifiutate',
                    text: `Il documento è stato ripristinato (${undoCount} operazioni annullate).`
                });
            }
        });
    };

    const _save = async () => {
        if (!editorInstance.current) {
            return;
        }

        setSaving(true);

        MySwal.fire({
            title: t('saving', { ns: 'labels' }) || 'Salvataggio in corso...',
            html: t('savingMessage', { ns: 'labels' }) || 'Attendere prego mentre il documento viene salvato.',
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                MySwal.showLoading();
            },
            showConfirmButton: false
        });

        try {
            const outputData = await editorInstance.current.save();

            const result = await api.sendMessage("editor/save", {
                user: currentUser(),
                body: {
                    id: id,
                    editorData: outputData
                }
            });

            if (result.success) {
                setHasUnsavedChanges(false);

                // Chiudi la modale di caricamento e mostra il messaggio di successo
                MySwal.fire({
                    title: t('success', { ns: 'labels' }),
                    icon: "success",
                    text: result.message,
                    showConfirmButton: true,
                    timer: 2000
                }).then(() => {
                    // todo 
                    if (result.fileId) {
                        window.location.href = `/editor/${result.fileId}`;
                    }
                });

                console.log("Document saved successfully:", result.data);
            } else {
                throw new Error(result.message || result.error || t('saveError', { ns: 'labels' }));
            }
        } catch (error) {
            console.error('Saving failed: ', error);

            // Chiudi la modale di caricamento e mostra l'errore
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                icon: "error",
                text: error.message || t('saveError', { ns: 'labels' }) || 'Errore durante il salvataggio',
                showConfirmButton: true
            });
        } finally {
            setSaving(false);
        }
    };

    const _getData = async () => {
        if (!id) return;
        try {

            var result = await api.sendMessage("editor/get", {
                user: currentUser(),
                body: {
                    id
                }
            });

            if (!result.success) {
                withReactContent(Swal).fire({
                    title: t('error', { ns: 'labels' }),
                    icon: "error",
                    text: result.message,
                    showConfirmButton: true,
                    allowOutsideClick: false,
                }).then(async (result) => {
                    if (result.isConfirmed) {
                     window.location.href ='/';
                    }
                  });
            }

            setData(result.data);
        }
        catch (e) {
            console.error(e);
        }
    }

    const _insertText = (tag) => {
        document.execCommand(tag, false, null);
    };

    const _changeColor = (color) => {
        document.execCommand('foreColor', false, color);
    };

    // Track editor changes to detect user modifications
    const handleEditorChange = async () => {
        if (!isAiModifying && !isRestoringFromHistory) {
            setHasUnsavedChanges(true);
            
            // Save snapshot on user changes (debounced to avoid too many snapshots)
            if (handleEditorChange.timeoutId) {
                clearTimeout(handleEditorChange.timeoutId);
            }
            
            handleEditorChange.timeoutId = setTimeout(async () => {
                await saveSnapshot();
            }, 2000); // Wait 2 seconds after user stops typing
        }
    };

    // Auto-save functionality (optional)
    const triggerAutoSave = () => {
        if (hasUnsavedChanges && !saving && !isAiModifying) {
            // You could implement an auto-save here
            console.log('Auto-save could be triggered here');
        }
    };

    // Warn user about unsaved changes when leaving
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = 'Hai modifiche non salvate. Sei sicuro di voler uscire?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    // Handle keyboard shortcuts for undo/redo
    useEffect(() => {
        const handleKeyDown = async (e) => {
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
                e.preventDefault();
                await undo();
            } else if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'Z') || 
                      ((e.ctrlKey || e.metaKey) && e.key === 'y')) {
                e.preventDefault();
                await redo();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [historyIndex, documentHistory.length]);

    //------------------------------------------------------------------------------
    // This effect runs once on mount. It writes the user menu and fetches the editor data (if an id is present).
    useEffect(() => {
        setHeaderHeight(calculateHeaderHeight());
        menu.write({ user: currentUser() });
        _getData();

        const footer = document.querySelector(".footer");
        if (footer) footer.style.display = "none";

        return () => {
            if (footer) footer.style.display = "";
        };
    }, []);



    // This effect initializes the EditorJS instance when the data is loaded and the editor DOM ref is available.
    // It also cleans up the EditorJS instance when the component unmounts or data changes.
    useEffect(() => {
        if (!editorRef.current) {
            return;
        }

        if (!editorInstance.current) {
            const editor = new EditorJS({
                holder: editorRef.current,
                tools: {
                    header: {
                        class: Header,
                        config: {
                            placeholder: 'Inserisci titolo...',
                            levels: [1, 2, 3, 4, 5, 6],
                            defaultLevel: 2
                        }
                    },
                    list: {
                        class: List,
                        inlineToolbar: true,
                        config: {
                            defaultStyle: 'unordered'
                        }
                    },
                    quote: {
                        class: Quote,
                        inlineToolbar: true,
                        shortcut: 'CMD+SHIFT+O',
                        config: {
                            quotePlaceholder: 'Inserisci citazione',
                            captionPlaceholder: 'Autore della citazione',
                        },
                    },
                    delimiter: Delimiter,
                    table: {
                        class: Table,
                        inlineToolbar: true,
                        config: {
                            rows: 2,          // Righe iniziali
                            cols: 3,          // Colonne iniziali  
                            maxRows: 15,      // Massimo righe
                            maxCols: 10,      // Massimo colonne
                            withHeadings: true,  // Abilita intestazioni
                            stretched: false     // Tabella non estesa
                        }
                    },
                    code: {
                        class: CodeTool,
                        shortcut: 'CMD+SHIFT+D'
                    },
                    inlineCode: {
                        class: InlineCode,
                        shortcut: 'CMD+SHIFT+M',
                    },
                    marker: {
                        class: Marker,
                        shortcut: 'CMD+SHIFT+H',
                    },
                    underline: Underline,
                },
                data: data || {},
                placeholder: 'Inizia a scrivere...',
                onChange: handleEditorChange, // Track changes
                onReady: () => {
                    // Save initial snapshot when editor is ready
                    setTimeout(async () => {
                        await saveSnapshot();
                    }, 500);
                }
            });
            editorInstance.current = editor;
        }

        return () => {
            if (editorInstance.current && typeof editorInstance.current.destroy === 'function') {
                editorInstance.current.destroy();
                editorInstance.current = null;
            }
        };
    }, [data]);

    // Auto-save effect (runs every 30 seconds if there are unsaved changes)
    useEffect(() => {
        const interval = setInterval(() => {
            triggerAutoSave();
        }, 30000); // 30 seconds

        return () => clearInterval(interval);
    }, [hasUnsavedChanges, saving, isAiModifying]);

    // Highlight AI changed blocks effect
    useEffect(() => {
        if (!editorInstance.current || pendingAiChanges.size === 0) {
            // Remove all AI highlighting if no pending changes
            const allBlocks = document.querySelectorAll('.ce-block[data-ai-changed="true"]');
            allBlocks.forEach(block => {
                block.removeAttribute('data-ai-changed');
                // Remove AI controls if they exist
                const controls = block.querySelector('.ai-change-controls');
                if (controls) controls.remove();
            });
            return;
        }

        // Get all affected block IDs from pending changes
        const affectedBlockIds = new Set();
        pendingAiChanges.forEach(change => {
            change.affectedBlockIds?.forEach(id => affectedBlockIds.add(id));
        });

        // Apply highlighting to affected blocks
        setTimeout(() => {
            const allBlocks = document.querySelectorAll('.ce-block');
            allBlocks.forEach(blockElement => {
                const blockData = editorInstance.current?.blocks?.getBlockByIndex(
                    Array.from(blockElement.parentNode.children).indexOf(blockElement)
                );

                if (blockData && affectedBlockIds.has(blockData.id)) {
                    blockElement.setAttribute('data-ai-changed', 'true');

                    // Add AI controls if not already present
                    if (!blockElement.querySelector('.ai-change-controls')) {
                        const controls = document.createElement('div');
                        controls.className = 'ai-change-controls';
                        controls.innerHTML = `
                            <button class="btn btn-sm btn-success me-1" data-action="accept" data-change-id="${Array.from(pendingAiChanges.values()).find(c => c.affectedBlockIds?.includes(blockData.id))?.id}" title="Accetta modifica">
                                <i class="fas fa-check"></i>
                            </button>
                            <button class="btn btn-sm btn-danger" data-action="reject" data-change-id="${Array.from(pendingAiChanges.values()).find(c => c.affectedBlockIds?.includes(blockData.id))?.id}" title="Rifiuta modifica">
                                <i class="fas fa-times"></i>
                            </button>
                        `;

                        // Add event listeners
                        controls.addEventListener('click', (e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            const action = e.target.closest('[data-action]')?.dataset.action;
                            const changeId = e.target.closest('[data-change-id]')?.dataset.changeId;

                            if (action && changeId) {
                                if (action === 'accept') {
                                    handleAiChangeAccepted(changeId);
                                } else if (action === 'reject') {
                                    handleAiChangeRejected(changeId);
                                }
                            }
                        });

                        blockElement.style.position = 'relative';
                        blockElement.appendChild(controls);
                    }
                } else {
                    blockElement.removeAttribute('data-ai-changed');
                    const controls = blockElement.querySelector('.ai-change-controls');
                    if (controls) controls.remove();
                }
            });
        }, 100); // Small delay to ensure blocks are rendered
    }, [pendingAiChanges]);

    //------------------------------------------------------------------------------
    return (
        <>
            <div className="container-fluid editor-container" style={{ height: `calc(100vh - ${headerHeight}px)` }}>
                <EditorToolbar
                    fontFamily={fontFamily}
                    setFontFamily={setFontFamily}
                    fontSize={fontSize}
                    setFontSize={setFontSize}
                    onInsertText={_insertText}
                    onChangeColor={_changeColor}
                    isAiModifying={isAiModifying}
                    hasUnsavedChanges={hasUnsavedChanges}
                    saving={saving}
                    showChat={showChat}
                    setShowChat={setShowChat}
                    onSave={_save}
                    onUndo={undo}
                    onRedo={redo}
                    canUndo={historyIndex > 0}
                    canRedo={historyIndex < documentHistory.length - 1}
                    pendingAiChanges={pendingAiChanges}
                />

                {/* Editor Container with Chat Panel */}
                <div className="editor-content-wrapper">
                    <PanelGroup direction="horizontal">
                        <Panel defaultSize={showChat ? 70 : 100} minSize={30}>
                            <div className="editor-panel-container">
                                {/* AI Modification Overlay */}
                                {isAiModifying && (
                                    <div className="editor-ai-modification-overlay">
                                        <div className="alert alert-info d-flex align-items-center">
                                            <div className="spinner-border text-info me-3" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <div>
                                                <strong>AI sta modificando il documento</strong><br />
                                                <small>Non modificare durante questo processo</small>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* AI Changes Overlay */}
                                {pendingAiChanges.size > 0 && (
                                    <div className="editor-ai-changes-overlay">
                                        <div className="alert alert-warning p-2 mb-2 editor-ai-changes-alert">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span>
                                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                                    {pendingAiChanges.size} modifica{pendingAiChanges.size > 1 ? 'he' : ''} AI in attesa
                                                </span>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btn-success"
                                                    onClick={handleAcceptAllAiChanges}
                                                    title="Accetta tutte le modifiche AI"
                                                >
                                                    <i className="fas fa-check me-1"></i>
                                                    Accetta tutto
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={handleRejectAllAiChanges}
                                                    title="Rifiuta tutte le modifiche AI"
                                                >
                                                    <i className="fas fa-times me-1"></i>
                                                    Rifiuta tutto
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div
                                    className={`editor-document-container ${isAiModifying ? 'ai-modifying' : ''}`}
                                    style={{
                                        fontFamily: fontFamily,
                                        fontSize: fontSize + 'px'
                                    }}
                                >
                                    <div
                                        ref={editorRef}
                                        className="editor-content"
                                    ></div>
                                </div>
                            </div>
                        </Panel>
                        {showChat && <>
                            <PanelResizeHandle className="editor-panel-resize-handle" />
                            <Panel defaultSize={30} minSize={20}>
                                <EditorMiniChat
                                    fileId={id}
                                    onDocumentUpdate={handleDocumentUpdate}
                                    editorInstance={editorInstance.current}
                                    onAiChangeAccepted={handleAiChangeAccepted}
                                    onAiChangeRejected={handleAiChangeRejected}
                                    pendingAiChanges={pendingAiChanges}
                                />
                            </Panel>
                        </>}
                    </PanelGroup>
                </div>
            </div>
        </>
    );
};

export default Editor; 